import { localizeDateNames } from "@/lib/dateNames";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLatest } from "@/hooks/useLatest";
import { api } from "@/lib/api";
import i18n from "@/i18n";
import { translateDetail } from "@/lib/apiErrorMessage";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Search, X, Trash2, Pencil, Power, Sparkles, Printer } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import RecipeImportDialog from "./RecipeImportDialog";
import { LinkSuggestionsBanner, LinkSuggestionsModal } from "./LinkSuggestions";
import { tFi } from "@/lib/printFi";

// Prompt 42 · Recipe Multi-Category. Chefs pick one OR several
// categories per recipe. The backend accepts both the new `categories`
// list AND the legacy `category` string on the same endpoint (kept in
// sync via mirroring), so existing recipes and older clients stay
// compatible.
const RECIPE_CATEGORIES = [
    "Meat", "Poultry", "Fish & Seafood", "Vegetarian", "Vegan",
    "Soup", "Pasta", "Bakery", "Dessert", "Breakfast",
    "Sauce", "Side Dish", "Main Course", "Buffet",
];
// Built-in recipe categories are canonical stored values — translate at display only.
const catLabel = (c) => (RECIPE_CATEGORIES.includes(c) ? i18n.t(`recipeCategories.${c.replace(/[^A-Za-z]/g, "")}`, c) : c);

export default function Recipes() {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const { scopedParams } = useRestaurantScope();
    const { confirm, ConfirmDialog } = useConfirm();
    const [tab, setTab] = useState("corporate");
    const [recipes, setRecipes] = useState([]);
    const [categories, setCategories] = useState(["All"]);
    const [category, setCategory] = useState("All");
    const [q, setQ] = useState("");
    const [searchMode, setSearchMode] = useState("name"); // name | ingredient
    const [includeInactive, setIncludeInactive] = useState(false);
    const [selected, setSelected] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    // Bumped every time a link/dismiss happens so the banner refetches.
    const [linkRefreshTick, setLinkRefreshTick] = useState(0);

    const load = async () => {
        const params = { recipe_type: tab, ...scopedParams };
        if (q && searchMode === "name") params.q = q;
        if (q && searchMode === "ingredient") params.ingredient = q;
        if (category !== "All") params.category = category;
        if (includeInactive) params.include_inactive = true;
        const { data } = await api.get("/recipes", { params });
        setRecipes(data);
    };

    useEffect(() => {
        // Prompt: Launch Audit R1 §3 — show the 13 simplified categories
        // instead of the legacy free-text list from the API. Legacy values
        // stored on existing recipes remain viewable in the list, we just
        // no longer offer them as filter chips.
        setCategories(["All", ...RECIPE_CATEGORIES]);
    }, []);

    const loadRef = useLatest(load);
    useEffect(() => { loadRef.current(); }, [tab, restaurantId, category, includeInactive, loadRef]);

    const openDetail = async (id) => {
        const { data } = await api.get(`/recipes/${id}`);
        setSelected(data);
    };

    const deleteRecipe = async (id) => {
        const ok = await confirm({
            title: t("recipes.archiveThisRecipe", "Archive this recipe?"),
            description: t("recipes.archivedRecipesStayInHistory", "Archived recipes stay in history but no longer appear in pickers. You can restore them later."),
            confirmLabel: "Archive",
            destructive: true,
        });
        if (!ok) return;
        await api.delete(`/recipes/${id}`);
        toast.success(t("recipes.recipeArchived", "Recipe archived"));
        setSelected(null);
        load();
    };

    const permanentDeleteRecipe = async (id, name) => {
        const ok = await confirm({
            title: t("recipes.permanentlyDeleteThisRecipe", "Permanently delete this recipe?"),
            description: t("recipes.deleteForeverDesc", { defaultValue: "“{{name}}” is archived and will be removed forever. This action cannot be undone.", name }),
            confirmLabel: t("recipes.deleteForever", "Delete forever"),
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/recipes/${id}/permanent`);
            toast.success(t("recipes.recipePermanentlyDeleted", "Recipe permanently deleted"));
            setSelected(null);
            load();
        } catch (e) {
            // Backend refuses when the recipe is still referenced by a
            // weekly-menu dish — surface the exact reason to the chef.
            toast.error(translateDetail(e.response?.data?.detail) || t("recipes.deleteFailed", "Delete failed"));
        }
    };

    const printRecipe = (recipe) => {
        // Kitchen-friendly A4 print: open a self-contained new tab so
        // the browser dialog never fights with modal / app CSS. Nothing
        // is persisted or recomputed — this reads the already-loaded
        // recipe object verbatim.
        _openRecipePrintWindow(recipe);
    };

    const activateRecipe = async (id) => {
        await api.post(`/recipes/${id}/activate`);
        toast.success(t("recipes.recipeActivated", "Recipe activated"));
        setSelected(null);
        load();
    };

    const startEdit = () => {
        setEditing(selected);
        setSelected(null);
        setFormOpen(true);
    };

    const startCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const canEditRecipe = (recipe) => {
        if (!recipe) return false;
        if (recipe.recipe_type === "corporate") return user.role === "corporate";
        if (recipe.recipe_type === "local" && user.role === "restaurant") {
            return recipe.restaurant_id === user.restaurant_id;
        }
        return true;
    };

    return (
        <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
            <PageHeader
                title={t("recipes.pageTitle", "Recipes")}
                subtitle={t("recipes.howIsThisDishPrepared", "How is this dish prepared?")}
                stackOnMobile
                action={
                    <div data-testid="recipes-header-actions" className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                        <button
                            data-testid="import-recipe-button"
                            onClick={() => setImportOpen(true)}
                            className="h-10 px-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-neutral-900 text-neutral-900 text-sm font-semibold hover:bg-neutral-100"
                        >
                            <Sparkles className="h-4 w-4" /> {t("recipes.importFromText", "Import from Text")}
                        </button>
                        <button
                            data-testid="add-recipe-button"
                            onClick={startCreate}
                            className="h-10 px-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                        >
                            <Plus className="h-4 w-4" /> {t("recipes.newRecipe", "New Recipe")}
                        </button>
                    </div>
                }
            />

            <LinkSuggestionsBanner
                key={linkRefreshTick}
                restaurantId={restaurantId}
                onOpen={() => setLinkModalOpen(true)} />

            <LinkSuggestionsModal
                open={linkModalOpen}
                restaurantId={restaurantId}
                onClose={() => setLinkModalOpen(false)}
                onLinked={() => {
                    setLinkRefreshTick((n) => n + 1);
                    load();
                }} />

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <input
                        data-testid="recipes-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder={searchMode === "name" ? t("recipes.searchRecipesByName", "Search recipes by name") : t("recipes.searchByIngredient", "Search by ingredient")}
                        className="w-full h-10 pl-9 pr-3 rounded-md border border-neutral-200 focus:outline-none focus:border-neutral-900"
                    />
                </div>
                <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden text-sm">
                    <button
                        data-testid="search-by-name"
                        onClick={() => { setSearchMode("name"); setTimeout(load, 0); }}
                        className={`px-3 h-10 font-semibold ${searchMode === "name" ? "bg-neutral-900 text-white" : "bg-white text-neutral-700"}`}
                    >{t("recipes.name", "Name")}</button>
                    <button
                        data-testid="search-by-ingredient"
                        onClick={() => { setSearchMode("ingredient"); setTimeout(load, 0); }}
                        className={`px-3 h-10 font-semibold ${searchMode === "ingredient" ? "bg-neutral-900 text-white" : "bg-white text-neutral-700"}`}
                    >{t("recipes.ingredient", "Ingredient")}</button>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-neutral-600 ml-auto" data-testid="toggle-inactive">
                    <input type="checkbox" checked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)} />
                    {t("recipes.showInactive", "Show inactive")}
                </label>
            </div>

            {/* Category chips */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
                {categories.map((c) => (
                    <button
                        key={c}
                        data-testid={`category-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setCategory(c)}
                        className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                            category === c
                                ? "bg-neutral-900 text-white border-neutral-900"
                                : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
                        }`}
                    >{c === "All" ? t("recipes.allCategories", "All") : catLabel(c)}</button>
                ))}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="corporate" data-testid="tab-corporate">{t("recipes.corporate", "Corporate")}</TabsTrigger>
                    <TabsTrigger value="local" data-testid="tab-local">{t("recipes.local", "Local")}</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-6">
                    {recipes.length === 0 ? (
                        <div className="kop-card p-8 text-sm text-neutral-500" data-testid="recipes-empty">
                            {t("recipes.noRecipesMatchTheCurrent", "No recipes match the current filters.")}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {recipes.map((r) => (
                                <button
                                    key={r.id}
                                    data-testid={`recipe-card-${r.id}`}
                                    onClick={() => openDetail(r.id)}
                                    className={`kop-card p-5 text-left hover:border-neutral-400 ${!r.active ? "opacity-60" : ""}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-display text-lg font-bold leading-tight">{r.name}</div>
                                        <StatusBadge status={r.recipe_type === "corporate" ? "completed" : "pending"}>
                                            {r.recipe_type === "corporate" ? t("recipes.scopeCorporate", "Corporate") : t("recipes.scopeLocal", "Local")}
                                        </StatusBadge>
                                    </div>
                                    <div className="text-xs text-neutral-500 mt-2 flex items-center gap-1.5 flex-wrap"
                                         data-testid={`recipe-card-${r.id}-categories`}>
                                        {((r.categories && r.categories.length)
                                            ? r.categories
                                            : (r.category ? [r.category] : []))
                                          .map((c) => (
                                            <span key={c}
                                                className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-[11px] font-medium text-neutral-700">
                                                {catLabel(c)}
                                            </span>
                                        ))}
                                        {!r.active && <StatusBadge status="action_required">{t("recipes.inactive", "inactive")}</StatusBadge>}
                                    </div>
                                    {r.description && (
                                        <p className="mt-3 text-sm text-neutral-600 line-clamp-2">{r.description}</p>
                                    )}
                                    <div className="text-xs text-neutral-400 mt-3">
                                        {t("recipes.baseYield", "Base yield:")} {r.base_yield_quantity} {r.base_yield_unit}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Detail */}
            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-2xl" data-testid="recipe-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl font-bold">{selected.name}</DialogTitle>
                                <div className="flex gap-2 mt-2 flex-wrap items-center">
                                    <StatusBadge status={selected.recipe_type === "corporate" ? "completed" : "pending"}>
                                        {selected.recipe_type === "corporate" ? t("recipes.scopeCorporate", "Corporate") : t("recipes.scopeLocal", "Local")}
                                    </StatusBadge>
                                    <div className="flex items-center gap-1.5 flex-wrap"
                                         data-testid="recipe-detail-categories">
                                        {((selected.categories && selected.categories.length)
                                            ? selected.categories
                                            : (selected.category ? [selected.category] : []))
                                          .map((c) => (
                                            <span key={c}
                                                className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-[11px] font-medium text-neutral-700">
                                                {catLabel(c)}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-xs text-neutral-500">
                                        {t("recipes.baseYieldInline", "base yield")} {selected.base_yield_quantity} {selected.base_yield_unit}
                                    </span>
                                    {!selected.active && (
                                        <StatusBadge status="action_required">{t("recipes.inactive", "inactive")}</StatusBadge>
                                    )}
                                </div>
                                {selected.description && (
                                    <p className="mt-3 text-sm text-neutral-600">{selected.description}</p>
                                )}
                            </DialogHeader>
                            <div className="space-y-4 max-h-[55vh] overflow-y-auto">
                                <div>
                                    <h4 className="font-semibold text-sm mb-2">{t("recipes.ingredients", "Ingredients")}</h4>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs text-neutral-500">
                                                <th className="py-2 font-medium">{t("recipes.ingredient", "Ingredient")}</th>
                                                <th className="py-2 font-medium">{t("recipes.qty", "Qty")}</th>
                                                <th className="py-2 font-medium">{t("recipes.purchase", "Purchase")}</th>
                                                <th className="py-2 font-medium">{t("recipes.costing", "Costing")}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-neutral-100">
                                            {(selected.ingredients || []).map((i) => (
                                                <tr key={i.id}>
                                                    <td className="py-2 font-medium">{i.ingredient_name}</td>
                                                    <td className="py-2 text-neutral-600">{i.quantity} {i.unit}</td>
                                                    <td className="py-2 text-neutral-600">{i.include_in_purchase_needs ? "Yes" : "No"}</td>
                                                    <td className="py-2 text-neutral-600">{i.include_in_costing ? "Yes" : "No"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <RecipeCostSummary
                                    recipeId={selected.id}
                                    ingredientsDraft={selected.ingredients || []}
                                    baseYield={selected.base_yield_quantity}
                                />
                                {selected.instructions && (
                                    <div>
                                        <h4 className="font-semibold text-sm mb-2">{t("recipes.instructions", "Instructions")}</h4>
                                        <p className="text-sm text-neutral-700 whitespace-pre-line">{selected.instructions}</p>
                                    </div>
                                )}
                                {selected.notes && (
                                    <div>
                                        <h4 className="font-semibold text-sm mb-2">{t("recipes.notes", "Notes")}</h4>
                                        <p className="text-sm text-neutral-500">{selected.notes}</p>
                                    </div>
                                )}
                            </div>
                            <DialogFooter className="gap-2">
                                {/* Print is read-only — always available to anyone who
                                    can view the recipe (including corporate recipes
                                    seen by restaurant chefs). */}
                                <button
                                    data-testid={`print-recipe-${selected.id}`}
                                    onClick={() => printRecipe(selected)}
                                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                                >
                                    <Printer className="h-4 w-4" /> {t("recipes.print", "Print")}
                                </button>
                                {canEditRecipe(selected) && (
                                    <>
                                        <button
                                            data-testid={`edit-recipe-${selected.id}`}
                                            onClick={startEdit}
                                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                                        >
                                            <Pencil className="h-4 w-4" /> {t("recipes.edit", "Edit")}
                                        </button>
                                        {selected.active ? (
                                            <button
                                                data-testid={`deactivate-recipe-${selected.id}`}
                                                onClick={() => deleteRecipe(selected.id)}
                                                className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50"
                                            >
                                                <Trash2 className="h-4 w-4" /> {t("recipes.archive", "Archive")}
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    data-testid={`activate-recipe-${selected.id}`}
                                                    onClick={() => activateRecipe(selected.id)}
                                                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50"
                                                >
                                                    <Power className="h-4 w-4" /> {t("recipes.activate", "Activate")}
                                                </button>
                                                <button
                                                    data-testid={`delete-recipe-forever-${selected.id}`}
                                                    onClick={() => permanentDeleteRecipe(selected.id, selected.name)}
                                                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-red-300 bg-red-50 text-red-800 text-sm font-semibold hover:bg-red-100"
                                                    title={t("recipes.onlyArchivedRecipesCanBe", "Only archived recipes can be permanently deleted")}
                                                >
                                                    <Trash2 className="h-4 w-4" /> {t("recipes.deleteForever", "Delete forever")}
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <RecipeFormDialog
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSaved={() => { setFormOpen(false); load(); }}
                recipe={editing}
                categories={categories.filter((c) => c !== "All")}
            />
            <RecipeImportDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onSaved={() => { setImportOpen(false); load(); }}
            />
            <ConfirmDialog />
        </div>
    );
}

function RecipeFormDialog({ open, onClose, onSaved, recipe, categories }) {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const { withRestaurant, needsExplicit, requireSelection } = useRestaurantScope();
    const isEdit = !!recipe;
    const [name, setName] = useState("");
    // Multi-category (Prompt 42). Legacy single-string recipes hydrate
    // into a one-element list. Save always sends the full `categories`
    // array; the backend keeps the legacy `category` field in sync.
    const [selectedCats, setSelectedCats] = useState(["Main Course"]);
    const [type, setType] = useState("local");
    const [baseQty, setBaseQty] = useState(1);
    const [baseUnit, setBaseUnit] = useState("portion");
    const [description, setDescription] = useState("");
    const [instructions, setInstructions] = useState("");
    const [notes, setNotes] = useState("");
    const [ingredients, setIngredients] = useState([
        {
            ingredient_name: "", quantity: 0, unit: "g",
            include_in_purchase_needs: true, include_in_costing: true,
            // Smart-Import metadata — MUST round-trip through the edit
            // form so opening + saving a recipe never wipes an existing
            // product link. Populated by seedFromRecipe below and
            // (for manual entry) by the ingredient matcher dropdown.
            product_id: null, match_confidence: 0, user_confirmed: false,
            section: "", original_text: "", notes: "",
            // client-only: the linked product's display name for the
            // "Linked · <name>" pill. Never sent to the API.
            _linked_name: "",
        },
    ]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (recipe) {
            setName(recipe.name || "");
            const seeded = (recipe.categories && recipe.categories.length)
                ? recipe.categories
                : (recipe.category ? [recipe.category] : ["Main Course"]);
            setSelectedCats(seeded);
            setType(recipe.recipe_type || "local");
            setBaseQty(recipe.base_yield_quantity || 1);
            setBaseUnit(recipe.base_yield_unit || "portion");
            setDescription(recipe.description || "");
            setInstructions(recipe.instructions || "");
            setNotes(recipe.notes || "");
            setIngredients(
                (recipe.ingredients || []).length
                    ? recipe.ingredients.map((i) => ({
                        ingredient_name: i.ingredient_name,
                        quantity: i.quantity, unit: i.unit,
                        include_in_purchase_needs: i.include_in_purchase_needs !== false,
                        include_in_costing: i.include_in_costing !== false,
                        // Preserve Smart-Import metadata so that
                        // re-saving the recipe does NOT wipe the
                        // product link written during Import from Text.
                        product_id: i.product_id || null,
                        match_confidence: Number(i.match_confidence) || 0,
                        user_confirmed: !!i.user_confirmed,
                        section: i.section || "",
                        original_text: i.original_text || "",
                        notes: i.notes || "",
                        // If already linked, mirror the ingredient name
                        // (which was set to the product's canonical
                        // name at import time) into the display pill.
                        _linked_name: i.product_id ? (i.ingredient_name || "") : "",
                    }))
                    : [{
                        ingredient_name: "", quantity: 0, unit: "g",
                        include_in_purchase_needs: true, include_in_costing: true,
                        product_id: null, match_confidence: 0, user_confirmed: false,
                        section: "", original_text: "", notes: "", _linked_name: "",
                    }]
            );
        } else {
            setName(""); setSelectedCats(["Main Course"]); setType("local");
            setBaseQty(1); setBaseUnit("portion");
            setDescription(""); setInstructions(""); setNotes("");
            setIngredients([{
                ingredient_name: "", quantity: 0, unit: "g",
                include_in_purchase_needs: true, include_in_costing: true,
                product_id: null, match_confidence: 0, user_confirmed: false,
                section: "", original_text: "", notes: "", _linked_name: "",
            }]);
        }
    }, [open, recipe]);

    const updateIng = (idx, patch) => {
        setIngredients((prev) => prev.map((ing, i) => {
            if (i !== idx) return ing;
            const next = { ...ing, ...patch };
            // Any manual edit to the ingredient name AFTER it was linked
            // means the chef is renaming the row — drop the link so the
            // pill disappears and the matcher can re-run against the
            // new text. product_id is only set again by an explicit
            // "Use product" click on a suggestion.
            if ("ingredient_name" in patch
                && ing.product_id
                && patch.ingredient_name !== ing._linked_name) {
                next.product_id = null;
                next.match_confidence = 0;
                next.user_confirmed = false;
                next._linked_name = "";
            }
            return next;
        }));
    };

    const linkProduct = (idx, product) => {
        // Snap the ingredient name to the product's canonical name so
        // the row reads exactly the same as the catalogue entry (this
        // is the same convention Smart Recipe Import uses on save).
        setIngredients((prev) => prev.map((ing, i) => (
            i === idx
                ? {
                    ...ing,
                    ingredient_name: product.product_name || ing.ingredient_name,
                    unit: product.unit || ing.unit,
                    product_id: product.product_id,
                    match_confidence: Number(product.confidence) || 1.0,
                    user_confirmed: true,
                    _linked_name: product.product_name || "",
                }
                : ing
        )));
    };

    const unlinkProduct = (idx) => {
        setIngredients((prev) => prev.map((ing, i) => (
            i === idx
                ? { ...ing, product_id: null, match_confidence: 0,
                     user_confirmed: false, _linked_name: "" }
                : ing
        )));
    };

    const save = async () => {
        if (!name.trim()) { toast.error(t("recipes.recipeNameRequired", "Recipe name required")); return; }
        // A "local" recipe is bound to a restaurant. Corporate users must
        // therefore have one selected before saving. Corporate-scope recipes
        // (recipe_type === "corporate") stay at the company level and do not
        // need a restaurant.
        const recipeType = user.role === "corporate" ? type : "local";
        if (recipeType === "local" && !requireSelection()) return;
        setSaving(true);
        try {
            let body = {
                name,
                categories: selectedCats,
                base_yield_quantity: Number(baseQty), base_yield_unit: baseUnit,
                description, instructions, notes,
                ingredients: ingredients
                    .filter((i) => i.ingredient_name.trim())
                    .map((i) => ({
                        ingredient_name: i.ingredient_name,
                        quantity: Number(i.quantity),
                        unit: i.unit,
                        include_in_purchase_needs: !!i.include_in_purchase_needs,
                        include_in_costing: !!i.include_in_costing,
                        // Preserve Smart-Import / manual-match metadata so
                        // the backend doesn't wipe it on PATCH (which
                        // deletes+re-inserts recipe_ingredients from the
                        // payload).
                        product_id: i.product_id || null,
                        match_confidence: Number(i.match_confidence) || 0,
                        user_confirmed: !!i.user_confirmed,
                        section: i.section || "",
                        original_text: i.original_text || "",
                        notes: i.notes || "",
                    })),
            };
            if (isEdit) {
                // PATCH resolves scope from the existing recipe doc, so
                // restaurant_id is not required in the body.
                await api.patch(`/recipes/${recipe.id}`, body);
                toast.success(t("recipes.recipeUpdated", "Recipe updated"));
            } else {
                body.recipe_type = recipeType;
                // POST /recipes with recipe_type=="local" calls
                // restaurant_scope() on the payload — a corporate user
                // must therefore ship the selected restaurant_id or the
                // backend returns 400.
                if (recipeType === "local") body = withRestaurant(body);
                await api.post("/recipes", body);
                toast.success(t("recipes.recipeAdded", "Recipe added"));
            }
            onSaved();
        } catch (e) {
            toast.error(translateDetail(e.response?.data?.detail) || t("recipes.saveFailed", "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl" data-testid="recipe-form-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? t("recipes.editRecipe", "Edit Recipe") : t("recipes.newRecipe", "New Recipe")}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("recipes.name", "Name")}</label>
                        <input data-testid="recipe-name" value={name} onChange={(e) => setName(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {t("recipes.categories", "Categories")}
                        </label>
                        <div className="text-[11px] text-neutral-500 mb-1.5">
                            {t("recipes.pickCategoriesHint", { defaultValue: "Pick one or several. A recipe can be tagged, for example, as both {{a}} and {{b}}.", a: catLabel("Vegetarian"), b: catLabel("Buffet") })}
                        </div>
                        <div data-testid="recipe-categories-picker"
                             className="mt-1 flex flex-wrap gap-1.5">
                            {RECIPE_CATEGORIES.map((c) => {
                                const active = selectedCats.includes(c);
                                return (
                                    <button
                                        type="button"
                                        key={c}
                                        data-testid={`recipe-cat-chip-${c.toLowerCase().replace(/\s+/g, "-").replace(/&/g,"and")}`}
                                        onClick={() => setSelectedCats((prev) =>
                                            prev.includes(c)
                                                ? prev.filter((x) => x !== c)
                                                : [...prev, c]
                                        )}
                                        className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                                            active
                                                ? "bg-neutral-900 text-white border-neutral-900"
                                                : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
                                        }`}
                                    >
                                        {active && "✓ "}{catLabel(c)}
                                    </button>
                                );
                            })}
                            {/* Preserve any legacy value that isn't in the canonical list */}
                            {selectedCats
                                .filter((c) => !RECIPE_CATEGORIES.includes(c))
                                .map((c) => (
                                    <button
                                        type="button"
                                        key={c}
                                        onClick={() => setSelectedCats((prev) => prev.filter((x) => x !== c))}
                                        className="h-8 px-3 rounded-full text-xs font-semibold border bg-amber-50 text-amber-900 border-amber-200"
                                        title={t("recipes.legacyValueClickToRemove", "Legacy value — click to remove")}
                                    >
                                        ✓ {c} <span className="opacity-60">(legacy)</span>
                                    </button>
                                ))}
                        </div>
                        {selectedCats.length === 0 && (
                            <div className="mt-1 text-[11px] text-rose-600"
                                 data-testid="recipe-categories-empty">
                                {t("recipes.selectAtLeastOneCategory", "Select at least one category.")}
                            </div>
                        )}
                    </div>
                    {user.role === "corporate" && !isEdit && (
                        <div>
                            <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("recipes.type", "Type")}</label>
                            <select data-testid="recipe-type" value={type} onChange={(e) => setType(e.target.value)}
                                className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md">
                                <option value="corporate">{t("recipes.scopeCorporate", "Corporate")}</option>
                                <option value="local">{t("recipes.scopeLocal", "Local")}</option>
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("recipes.baseYieldQty", "Base yield qty")}</label>
                        <input type="number" data-testid="recipe-base-qty" value={baseQty}
                            onChange={(e) => setBaseQty(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("recipes.baseYieldUnit", "Base yield unit")}</label>
                        <input data-testid="recipe-base-unit" value={baseUnit}
                            onChange={(e) => setBaseUnit(e.target.value)}
                            placeholder="portion · GN tray · batch · kg · L"
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("recipes.description", "Description")}</label>
                        <input data-testid="recipe-description" value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                </div>

                <div className="mt-4">
                    <div className="text-xs uppercase tracking-wide font-semibold text-neutral-600 mb-2">{t("recipes.ingredients", "Ingredients")}</div>
                    <div className="hidden md:grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-neutral-500 px-1">
                        <div className="col-span-4">{t("recipes.name", "Name")}</div>
                        <div className="col-span-2">{t("recipes.qty", "Qty")}</div>
                        <div className="col-span-2">{t("recipes.unit", "Unit")}</div>
                        <div className="col-span-1 text-center">{t("recipes.purchase", "Purchase")}</div>
                        <div className="col-span-2 text-center">{t("recipes.costing", "Costing")}</div>
                        <div className="col-span-1"></div>
                    </div>
                    <div className="space-y-2 mt-1"
                          data-testid="ingredient-rows"
                          /* overflow-visible so the suggestion dropdown on
                             any ingredient row can escape the container.
                             The row list is naturally short for a real
                             recipe, so we intentionally don't cap height
                             + scroll here — a scroll container would clip
                             the absolutely-positioned suggestions. */>
                        {ingredients.map((ing, idx) => (
                            <IngredientRow
                                key={idx} idx={idx} ing={ing}
                                restaurantId={restaurantId}
                                onChange={(patch) => updateIng(idx, patch)}
                                onLink={(product) => linkProduct(idx, product)}
                                onUnlink={() => unlinkProduct(idx)}
                                onRemove={() =>
                                    setIngredients((p) => p.filter((_, i) => i !== idx))} />
                        ))}
                    </div>
                    <button
                        data-testid="add-ingredient-row"
                        onClick={() => setIngredients((p) => [...p, {
                            ingredient_name: "", quantity: 0, unit: "g",
                            include_in_purchase_needs: true, include_in_costing: true,
                            product_id: null, match_confidence: 0, user_confirmed: false,
                            section: "", original_text: "", notes: "", _linked_name: "",
                        }])}
                        className="mt-2 text-sm text-neutral-700 hover:text-neutral-900 font-semibold inline-flex items-center gap-1">
                        <Plus className="h-3.5 w-3.5" /> {t("recipes.addIngredient", "Add ingredient")}
                    </button>

                    <PlateWeight ingredients={ingredients} />
                    <RecipeCostSummary recipeId={recipe?.id}
                                          ingredientsDraft={ingredients}
                                          baseYield={baseQty} />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("recipes.instructions", "Instructions")}</label>
                        <textarea data-testid="recipe-instructions" rows={3} value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:border-neutral-900" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("recipes.notes", "Notes")}</label>
                        <textarea data-testid="recipe-notes" rows={2} value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                </div>

                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("recipes.cancel", "Cancel")}</button>
                    <button data-testid="save-recipe-button" onClick={save}
                        disabled={saving || selectedCats.length === 0}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("recipes.saving", "Saving…") : isEdit ? t("recipes.saveChanges", "Save changes") : t("recipes.saveRecipe", "Save recipe")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────────────────────────────────────────
// PlateWeight — Prompt 33 §2
// Sums ingredient quantities whose unit is a weight unit (g, kg, mg)
// and displays a small read-only "Total Plate Weight" line.
// Ingredients without a weight unit (l, ml, kpl, pcs, tbsp, tsp) are
// ignored — they cannot be sensibly aggregated into grams here.
// ─────────────────────────────────────────────────────────────
function PlateWeight({ ingredients }) {
    const { t } = useTranslation();
    // Convert everything to grams for a single tidy total.
    const toGrams = (qty, unit) => {
        const q = Number(qty) || 0;
        const u = (unit || "").toLowerCase().trim();
        if (u === "g" || u === "gr" || u === "gram")  return q;
        if (u === "kg" || u === "kilo" || u === "kilogram") return q * 1000;
        if (u === "mg") return q / 1000;
        return null; // non-weight → ignored
    };
    let totalGrams = 0;
    let counted = 0;
    for (const ing of (ingredients || [])) {
        const g = toGrams(ing.quantity, ing.unit);
        if (g !== null) { totalGrams += g; counted += 1; }
    }
    if (counted === 0) return null;
    const label = totalGrams >= 1000
        ? `${(totalGrams / 1000).toFixed(2).replace(/\.00$/, "")} kg`
        : `${Math.round(totalGrams)} g`;
    return (
        <div className="mt-3 pt-3 border-t border-dashed border-neutral-200
                        flex items-baseline justify-between text-sm"
             data-testid="recipe-plate-weight">
            <div>
                <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">
                    {t("recipes.totalPlateWeight", "Total Plate Weight")}
                </span>
                <span className="ml-2 text-[11px] text-neutral-400">
                    {t("recipes.weighedIngredientsOnly", "(weighed ingredients only)")}
                </span>
            </div>
            <div className="font-bold font-mono"
                 data-testid="recipe-plate-weight-value">{label}</div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────
// IngredientRow — one row inside the recipe editor.
//
// Renders name/qty/unit/purchase/costing controls PLUS:
//   • a green "Linked · <product>" pill when the row carries a
//     product_id (from Smart Recipe Import OR from a manual "Use
//     product" click here), with an X to unlink,
//   • an inline suggestion dropdown when the chef is typing an
//     un-linked ingredient — populated from the existing matcher at
//     `POST /api/recipes/ingredients/match`. Every match displays with
//     a "Use product" button; nothing auto-links.
// ─────────────────────────────────────────────────────────────
function IngredientRow({ idx, ing, restaurantId, onChange, onLink, onUnlink, onRemove }) {
    const { t } = useTranslation();
    const [matches, setMatches] = useState([]);
    const [showSuggest, setShowSuggest] = useState(false);
    const [loading, setLoading] = useState(false);

    const linked = !!ing.product_id;

    // Debounced product lookup. Only queries when: NOT linked, name
    // has ≥3 characters, and the ingredient name changed (not on
    // every re-render).
    useEffect(() => {
        if (linked) { setMatches([]); return; }
        const name = (ing.ingredient_name || "").trim();
        if (name.length < 3) { setMatches([]); return; }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const body = { ingredient_name: name, limit: 5 };
                if (restaurantId) body.restaurant_id = restaurantId;
                const { data } = await api.post("/recipes/ingredients/match", body);
                // Only surface rows that are linkable (have a product_id)
                // AND have a decent score. Recommendations without a
                // product row (source !== 'product') are dropped —
                // linking to a supplier alias doesn't help costing.
                const rows = (data?.matches || []).filter(
                    (m) => m.product_id && (m.confidence || 0) >= 0.55
                       && (m.source || "product") === "product");
                setMatches(rows);
            } catch (_e) {
                setMatches([]);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [ing.ingredient_name, linked, restaurantId]);

    return (
        <div className="grid grid-cols-12 gap-2 items-start">
            <div className="col-span-4 relative">
                <input
                    data-testid={`ingredient-name-${idx}`}
                    placeholder={t("recipes.ingredient", "Ingredient")}
                    value={ing.ingredient_name}
                    onFocus={() => setShowSuggest(true)}
                    onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
                    onChange={(e) => onChange({ ingredient_name: e.target.value })}
                    className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm"
                />
                {linked && (
                    <div className="mt-1 inline-flex items-center gap-1.5 rounded-full
                                     bg-emerald-50 border border-emerald-300 text-emerald-800
                                     text-[11px] font-semibold px-2 py-0.5"
                          data-testid={`ingredient-linked-${idx}`}>
                        <span>{t("recipes.linked", "Linked")} · {ing._linked_name || ing.ingredient_name}</span>
                        <button onClick={onUnlink}
                                data-testid={`ingredient-unlink-${idx}`}
                                title={t("recipes.unlinkProduct", "Unlink product")}
                                className="hover:text-red-700">
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                )}
                {!linked && showSuggest && matches.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 mt-1 rounded-md
                                     border border-neutral-200 bg-white shadow-lg
                                     max-h-56 overflow-y-auto"
                          data-testid={`ingredient-suggestions-${idx}`}>
                        {matches.map((m) => (
                            <div key={m.product_id}
                                  className="flex items-center justify-between gap-2
                                             px-3 py-2 text-xs border-b last:border-b-0
                                             border-neutral-100 hover:bg-neutral-50">
                                <div className="min-w-0">
                                    <div className="font-semibold text-neutral-900 truncate">
                                        {m.product_name}
                                    </div>
                                    <div className="text-neutral-500 text-[10px]">
                                        {m.unit || "—"} · {Math.round((m.confidence || 0) * 100)}% · {m.reason}
                                    </div>
                                </div>
                                <button
                                    data-testid={`ingredient-use-product-${idx}-${m.product_id}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => { onLink(m); setShowSuggest(false); }}
                                    className="shrink-0 h-7 px-2 rounded-md bg-neutral-900
                                                text-white text-[11px] font-semibold
                                                hover:bg-neutral-800">
                                    {t("recipes.useProduct", "Use product")}
                                </button>
                            </div>
                        ))}
                        {loading && (
                            <div className="px-3 py-2 text-[11px] text-neutral-500">
                                {t("recipes.searching", "Searching…")}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <input
                data-testid={`ingredient-qty-${idx}`}
                type="number" placeholder="Qty" value={ing.quantity}
                onChange={(e) => onChange({ quantity: e.target.value })}
                className="col-span-2 h-10 px-3 border border-neutral-300 rounded-md text-sm"
            />
            <input
                data-testid={`ingredient-unit-${idx}`}
                placeholder="Unit" value={ing.unit}
                onChange={(e) => onChange({ unit: e.target.value })}
                className="col-span-2 h-10 px-3 border border-neutral-300 rounded-md text-sm"
            />
            <div className="col-span-1 text-center pt-3">
                <input type="checkbox" data-testid={`ingredient-purchase-${idx}`}
                    checked={!!ing.include_in_purchase_needs}
                    onChange={(e) => onChange({ include_in_purchase_needs: e.target.checked })} />
            </div>
            <div className="col-span-2 text-center pt-3">
                <input type="checkbox" data-testid={`ingredient-costing-${idx}`}
                    checked={!!ing.include_in_costing}
                    onChange={(e) => onChange({ include_in_costing: e.target.checked })} />
            </div>
            <button data-testid={`remove-ingredient-${idx}`}
                onClick={onRemove}
                className="col-span-1 h-10 grid place-items-center rounded-md hover:bg-neutral-100">
                <X className="h-4 w-4 text-neutral-500" />
            </button>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────
// RecipeCostSummary — Launch Fix R5
// Compact, read-only estimated cost block that appears at the bottom of
// the Ingredients section. Never blocks saving. For not-yet-saved
// recipes, shows a hint to save first (server needs the recipe id to
// resolve product prices).
// ─────────────────────────────────────────────────────────────
function RecipeCostSummary({ recipeId, ingredientsDraft, baseYield }) {
    const { t } = useTranslation();
    const [cost, setCost] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [reloadTick, setReloadTick] = React.useState(0);

    const reload = React.useCallback(() => {
        setReloadTick((t) => t + 1);
    }, []);

    React.useEffect(() => {
        if (!recipeId) { setCost(null); return; }
        let alive = true;
        setLoading(true);
        api.get(`/recipes/${recipeId}/cost`)
            .then(({ data }) => alive && setCost(data))
            .catch(() => alive && setCost(null))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [recipeId, ingredientsDraft.length, reloadTick]);

    const fmt = (v) => v == null ? "—" : `€ ${Number(v).toFixed(2)}`;
    const fmtDate = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return localizeDateNames(d.toLocaleDateString(undefined,
                { year: "numeric", month: "short", day: "2-digit" }));
        } catch (_e) { return "—"; }
    };

    if (!recipeId) {
        return (
            <div data-testid="recipe-cost-summary-empty"
                 className="mt-4 rounded-md border border-dashed border-neutral-200 p-3 text-xs text-neutral-500">
                {t("recipes.saveTheRecipeToSee", "Save the recipe to see the estimated cost from your latest confirmed delivery prices.")}
            </div>
        );
    }
    if (loading || !cost) {
        return (
            <div data-testid="recipe-cost-summary-loading"
                 className="mt-4 rounded-md border border-neutral-200 p-3 text-xs text-neutral-500 animate-pulse">
                {t("recipes.loadingEstimatedCost", "Loading estimated cost…")}
            </div>
        );
    }
    const coverage = cost.total_count > 0
        ? t("recipes.coverageN", { defaultValue: "{{priced}} of {{total}} ingredients",
            priced: cost.priced_count, total: cost.total_count })
        : "—";
    const isPartial = cost.total_count > 0 && cost.priced_count < cost.total_count;

    // Total weight = sum of ingredient qty converted to grams, when the
    // unit is a mass or volume unit we can normalise. This is a pure UI
    // aggregation over the already-visible ingredient list — it never
    // touches the costing engine and never persists.
    const massToGrams = (q, u) => {
        const n = Number(q); if (!Number.isFinite(n) || n === 0) return null;
        const unit = String(u || "").trim().toLowerCase();
        if (unit === "g") return n;
        if (unit === "kg") return n * 1000;
        if (unit === "mg") return n / 1000;
        if (unit === "ml") return n;            // approx 1g / ml for water-like
        if (unit === "cl") return n * 10;
        if (unit === "dl") return n * 100;
        if (unit === "l" || unit === "lt") return n * 1000;
        return null;                             // pcs, tsp, tbsp, kpl → skip
    };
    let totalG = 0;
    let weighed = 0;
    let skipped = 0;
    for (const it of (ingredientsDraft || [])) {
        const g = massToGrams(it.quantity, it.unit);
        if (g == null) skipped += 1;
        else { totalG += g; weighed += 1; }
    }
    const weightLabel = weighed === 0 ? "—"
        : totalG >= 1000
            ? `${(totalG / 1000).toFixed(2)} kg`
            : `${Math.round(totalG)} g`;

    return (
        <div data-testid="recipe-cost-summary"
             className="mt-4 rounded-md border border-neutral-200 bg-neutral-50/60 p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <CostCell testId="recipe-total-weight"
                          label={t("recipes.totalWeight", "Total Weight")}
                          value={weightLabel}
                          sub={skipped > 0
                              ? t("recipes.nonMassItems", { defaultValue: "{{count}} items in non-mass units", count: skipped })
                              : undefined} />
                <CostCell testId="recipe-cost-estimated"
                          label={isPartial ? t("recipes.partialEstimatedCost", "Partial Estimated Cost") : t("recipes.estimatedRecipeCost", "Estimated Recipe Cost")}
                          value={fmt(cost.estimated_cost)}
                          sub={isPartial ? t("recipes.partialCostBasis", {
                              defaultValue: "Only {{priced}} of {{total}} ingredients can be costed",
                              priced: cost.priced_count, total: cost.total_count }) : undefined}
                          tone={isPartial ? "warn" : "good"} />
                <CostCell testId="recipe-cost-per-portion"
                          label={isPartial ? t("recipes.partialCostPerPortion", "Partial Cost per Portion") : t("recipes.costPerPortion", "Cost per Portion")}
                          value={fmt(cost.cost_per_portion)}
                          sub={[
                              baseYield > 0 ? t("recipes.baseYieldN", { defaultValue: "Base yield {{n}}", n: baseYield }) : t("recipes.noBaseYield", "No base yield"),
                              isPartial ? t("recipes.partialPortionBasis", "from the partial total") : null,
                          ].filter(Boolean).join(" · ")}
                          tone={isPartial ? "warn" : undefined} />
                <CostCell testId="recipe-cost-coverage"
                          label={t("recipes.priceCoverage", "Price Coverage")}
                          value={coverage}
                          sub={isPartial ? t("recipes.costIncompleteSomeIngredientsAre", "Cost incomplete – some ingredients are not linked") : undefined}
                          tone={isPartial ? "warn" : "good"} />
                <CostCell testId="recipe-cost-last-update"
                          label={t("recipes.lastCostUpdate", "Last Cost Update")}
                          value={fmtDate(cost.last_cost_update)}
                          sub={t("recipes.fromConfirmedDeliveries", "From confirmed deliveries")} />
            </div>

            {cost.missing.length > 0 && (
                <div className="mt-3 text-xs text-neutral-600"
                     data-testid="recipe-cost-missing">
                    <div className="font-semibold text-amber-700 mb-1">
                        {t("recipes.missingPriceData", "Missing price data:")}
                    </div>
                    <ul className="list-disc pl-5 space-y-1">
                        {cost.missing.map((m, i) => (
                            <li key={i}
                                data-testid={`recipe-cost-missing-row-${i}`}>
                                <span className="font-medium">{m.ingredient_name}</span>
                                {m.reason === "unit_mismatch" && (
                                    <span className="text-neutral-500">
                                        {" "}{t("recipes.unitMismatchNote", {
                                            defaultValue: "(unit mismatch — recipe uses {{recipeUnit}}, product stored in {{productUnit}}; not included in the cost)",
                                            recipeUnit: m.recipe_unit, productUnit: m.product_unit })}
                                    </span>
                                )}
                                {m.reason === "missing_price" && m.product_id && (
                                    <>
                                        <span className="text-neutral-500">
                                            {" "}· linked to <b>{m.product_name}</b>, no purchase price yet.
                                        </span>
                                        <AddPriceInline
                                            productId={m.product_id}
                                            productName={m.product_name}
                                            canonicalUnit={m.product_unit}
                                            onSaved={reload}
                                        />
                                    </>
                                )}
                                {m.reason === "no_product" && (
                                    <span className="text-neutral-500">
                                        {" "}· not linked to any product.
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// Inline Add-Price row rendered per missing ingredient in the Recipe
// Cost card. Persists on the linked PRODUCT via `PATCH
// /products/{id}/price` — one product update propagates to every recipe
// linked to it. Only offers units compatible with the product's
// canonical unit; incompatible units are rejected at the backend too.
function AddPriceInline({ productId, productName, canonicalUnit, onSaved }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [price, setPrice] = useState("");
    const [unit, setUnit] = useState(canonicalUnit || "");
    const [units, setUnits] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get(
                    `/products/${productId}/compatible-units`);
                setUnits(data.compatible_units || []);
                setUnit((cur) => cur || data.canonical_unit || canonicalUnit || "");
            } catch { setUnits(canonicalUnit ? [canonicalUnit] : []); }
        })();
    }, [open, productId, canonicalUnit]);

    const save = async () => {
        const num = parseFloat(price);
        if (!(num > 0) || !unit) return;
        setSaving(true);
        try {
            await api.patch(`/products/${productId}/price`,
                { price: num, unit });
            toast.success(t("recipes.priceSet", { defaultValue: "Price set on {{name}}. All linked recipes updated.", name: productName }));
            setOpen(false); setPrice("");
            onSaved && await onSaved();
        } catch (e) {
            toast.error(translateDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    if (!open) {
        return (
            <button
                data-testid={`add-price-open-${productId}`}
                onClick={() => setOpen(true)}
                className="ml-2 text-xs font-semibold text-neutral-900 underline underline-offset-2 hover:no-underline"
            >
                {t("recipes.addPrice", "Add price")}
            </button>
        );
    }
    return (
        <div className="mt-1 flex items-center gap-2"
             data-testid={`add-price-form-${productId}`}>
            <span className="text-xs text-neutral-500">€</span>
            <input
                data-testid={`add-price-value-${productId}`}
                type="number" min="0" step="0.0001"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="h-8 w-24 px-2 text-sm border border-neutral-300 rounded-md"
                placeholder="e.g. 3.50"
            />
            <span className="text-xs text-neutral-500">/</span>
            <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid={`add-price-unit-${productId}`}
                                 className="h-8 w-20 text-sm">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {units.map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <button
                data-testid={`add-price-save-${productId}`}
                onClick={save} disabled={saving || !price}
                className="h-8 px-3 rounded-md bg-neutral-900 text-white text-xs font-semibold disabled:opacity-40"
            >
                {saving ? t("recipes.saving", "Saving…") : t("recipes.save", "Save")}
            </button>
            <button
                onClick={() => { setOpen(false); setPrice(""); }}
                className="h-8 px-2 text-xs text-neutral-500 hover:text-neutral-900"
            >
                {t("recipes.cancel", "Cancel")}
            </button>
        </div>
    );
}

function CostCell({ testId, label, value, sub, tone }) {
    const toneClass = tone === "warn" ? "text-amber-700"
                    : tone === "good" ? "text-neutral-900"
                    : "text-neutral-900";
    return (
        <div data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">
                {label}
            </div>
            <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
            {sub && <div className="text-[11px] text-neutral-500 mt-0.5">{sub}</div>}
        </div>
    );
}




// -------------------------------------------------------------------
// Print single recipe
//
// Opens a new browser tab with a self-contained, kitchen-friendly A4
// layout of the current recipe and triggers window.print(). No UI
// chrome, no colors, big fonts, generous line spacing so line cooks
// can read it on the pass. Nothing is persisted or recalculated —
// values come from the recipe object already loaded in the modal.
// -------------------------------------------------------------------
function _openRecipePrintWindow(recipe) {
    if (!recipe) return;
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) {
        // Popup blocker fired — fall back to a hidden iframe so the
        // chef still gets a printable view without leaving the page.
        _fallbackIframePrint(recipe);
        return;
    }
    w.document.open();
    w.document.write(_buildRecipePrintHtml(recipe));
    w.document.close();
    w.addEventListener("load", () => {
        try { w.focus(); w.print(); } catch { /* user cancelled */ }
    });
    setTimeout(() => {
        try { w.focus(); w.print(); } catch { /* ignore */ }
    }, 300);
}

function _fallbackIframePrint(recipe) {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(_buildRecipePrintHtml(recipe));
    doc.close();
    iframe.onload = () => {
        try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } finally {
            setTimeout(() => document.body.removeChild(iframe), 500);
        }
    };
}

function _escapeHtml(s) {
    return String(s == null ? "" : s)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll("\"", "&quot;")
        .replaceAll("'", "&#39;");
}

function _buildRecipePrintHtml(recipe) {
    const ing = (recipe.ingredients || []).map((i) => `
        <li>
            <span class="qty">${_escapeHtml(i.quantity ?? "")} ${_escapeHtml(i.unit || "")}</span>
            <span class="name">${_escapeHtml(i.ingredient_name || i.name || "")}</span>
            ${i.notes ? `<span class="note">- ${_escapeHtml(i.notes)}</span>` : ""}
        </li>`).join("");
    const proc = (recipe.instructions || "").split(/\r?\n/)
        .map((line) => line.trim()).filter(Boolean)
        .map((line) => `<li>${_escapeHtml(line)}</li>`).join("");
    const yieldLine = (recipe.base_yield_quantity && recipe.base_yield_unit)
        ? `${recipe.base_yield_quantity} ${_escapeHtml(recipe.base_yield_unit)}`
        : "";
    return `<!doctype html>
<html lang="fi">
<head>
    <meta charset="utf-8" />
    <title>${_escapeHtml(recipe.name || tFi("weeklyMenu.recipe"))}</title>
    <style>
        @page { size: A4; margin: 18mm 20mm 22mm 20mm; }
        html, body { margin: 0; padding: 0; background: #fff; color: #000;
                      font-family: Georgia, "Times New Roman", serif;
                      font-size: 14pt; line-height: 1.55; }
        header { border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 20px; }
        h1 { font-size: 26pt; margin: 0 0 4px; font-weight: 700; }
        .meta { font-size: 11pt; color: #444; }
        .meta span + span::before { content: " · "; }
        p.desc { margin: 12px 0 0; font-size: 13pt; color: #222; font-style: italic; }
        h2 { font-size: 15pt; margin: 22px 0 8px; font-weight: 700;
              text-transform: uppercase; letter-spacing: 0.04em;
              border-bottom: 1px solid #000; padding-bottom: 3px; }
        ul.ingredients { list-style: none; padding: 0; margin: 0;
                          columns: 2; column-gap: 24px; }
        ul.ingredients li { break-inside: avoid; padding: 2px 0 4px;
                              border-bottom: 1px dotted #999;
                              display: flex; gap: 10px; align-items: baseline; }
        ul.ingredients li .qty { min-width: 90px; font-weight: 700; }
        ul.ingredients li .name { flex: 1; }
        ul.ingredients li .note { color: #666; font-style: italic; }
        ol.procedure { padding-left: 22px; margin: 0; }
        ol.procedure li { padding: 3px 0 6px; }
        footer { position: fixed; bottom: 8mm; left: 20mm; right: 20mm;
                  border-top: 1px solid #ccc; padding-top: 4px;
                  font-size: 9pt; color: #888;
                  display: flex; justify-content: space-between; }
    </style>
</head>
<body>
    <header>
        <h1>${_escapeHtml(recipe.name || tFi("recipes.printUntitled"))}</h1>
        <div class="meta">
            ${(() => {
                const cats = (recipe.categories && recipe.categories.length)
                    ? recipe.categories
                    : (recipe.category ? [recipe.category] : []);
                return cats.length
                    ? `<span>${cats.map(_escapeHtml).join(" · ")}</span>`
                    : "";
            })()}
            ${yieldLine ? `<span>${tFi("recipes.printYield")}: ${yieldLine}</span>` : ""}
        </div>
        ${recipe.description
            ? `<p class="desc">${_escapeHtml(recipe.description)}</p>` : ""}
    </header>

    ${ing ? `<h2>${tFi("recipes.ingredients")}</h2><ul class="ingredients">${ing}</ul>` : ""}
    ${proc ? `<h2>${tFi("recipes.printProcedure")}</h2><ol class="procedure">${proc}</ol>` : ""}
    ${recipe.notes
        ? `<h2>${tFi("recipes.notes")}</h2><p>${_escapeHtml(recipe.notes)}</p>` : ""}

    <footer>
        <span>${_escapeHtml(recipe.name || "")}</span>
        <span>MiseOne · ${new Date().toLocaleDateString("fi-FI")}</span>
    </footer>
</body>
</html>`;
}
